/* eslint-disable react/prop-types */
import { Box, Button } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import LaunchIcon from '@material-ui/icons/Launch';
import cx from 'classnames';
import { PropTypes } from 'prop-types';
import React, { useCallback } from 'react';
import { styles } from './certificate-review.styles';

const DEFAULT_NAME = 'Elevation Certificate.pdf';

const renderPdfDocument = (pdfDocument, documentName, pdfViewerHeight) => {
    if (!pdfDocument) {
        return null;
    }

    // NB: In the case of PB, this will be a Blob URL, but on dashboard this will
    // be a B64 string. Ideally we should pass a Blob URL in all cases, though.
    const pdf = pdfDocument.replace(
        'data:application;base64',
        'data:application/pdf;base64'
    );

    return (
        <embed
            title={documentName}
            src={`${pdf}#navpanes=0&toolbar=1&scrollbar=1`}
            width="100%"
            height={pdfViewerHeight}
            type="application/pdf"
        />
    );
};

const CertificateReviewComponent = props => {
    const { classes = false } = props;

    const handleOpenInNewTab = useCallback(sourceBase64 => {
        const isArrayOfBase64 =
            Array.isArray(sourceBase64) && sourceBase64.length > 0;
        const isImage = isArrayOfBase64
            ? sourceBase64[0].startsWith('data:image;base64,')
            : sourceBase64.startsWith('data:image;base64,');
        const isPdf = isArrayOfBase64
            ? sourceBase64[0].startsWith('data:application/pdf;base64,')
            : sourceBase64.startsWith('data:application/pdf;base64,');
        const isBlob = isArrayOfBase64
            ? sourceBase64[0].startsWith('blob:')
            : sourceBase64.startsWith('blob:');

        let rawBase64;
        if (isImage) {
            rawBase64 = isArrayOfBase64
                ? sourceBase64[0].replace('data:image;base64,', '')
                : sourceBase64.replace('data:image;base64,', '');
        }
        if (isPdf) {
            rawBase64 = isArrayOfBase64
                ? sourceBase64[0].replace('data:application/pdf;base64,', '')
                : sourceBase64.replace('data:application/pdf;base64,', '');
        }
        if (isBlob) {
            rawBase64 = isArrayOfBase64 ? sourceBase64[0] : sourceBase64;
        }

        if (isPdf) {
            try {
                const binary = window.atob(rawBase64);
                const byteArray = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i += 1) {
                    byteArray[i] = binary.charCodeAt(i);
                }

                const file = new window.Blob([byteArray], {
                    type: isImage ? 'image;base64,' : 'application/pdf;base64'
                });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            } catch {
                // eslint-disable-next-line no-console
                console.warn('Unable to open PDF document');
            }

            return;
        }

        if (isBlob) {
            window.open(rawBase64);

            return;
        }

        if (isImage) {
            try {
                const image = new window.Image();
                image.src = `data:image/jpg;base64,${rawBase64}`;

                const newWindow = window.open('');
                newWindow.document.write(image.outerHTML);
            } catch {
                // eslint-disable-next-line no-console
                console.warn('Unable to open image document');
            }
        }
    }, []);

    const {
        documentBase64,
        documentName = DEFAULT_NAME,
        pdfViewerHeight
    } = props;

    const launchButtonClass = cx(classes.buttonStyle, classes.buttonLaunch);

    const renderImageAsDocument = docBase64 => {
        let base64DocumentsArray = docBase64;
        if (typeof docBase64 === 'string') {
            base64DocumentsArray = [docBase64];
        }

        return base64DocumentsArray.map((content, pageNo) => (
            <img
                src={content}
                // eslint-disable-next-line react/no-array-index-key
                key={pageNo}
                alt={`Certificate page #${pageNo}`}
                className={classes.documentImg}
                draggable={false}
            />
        ));
    };

    const renderImagesAsDocOrAPdf = () => {
        if (!documentBase64) {
            return null;
        }

        if (Array.isArray(documentBase64)) {
            if (documentBase64.every(doc => doc.startsWith('data:image'))) {
                return renderImageAsDocument(documentBase64);
            }

            const latestDocumentUploaded =
                documentBase64[documentBase64.length - 1] || '';
            if (latestDocumentUploaded.startsWith('data:application')) {
                return renderPdfDocument(
                    latestDocumentUploaded,
                    documentName,
                    pdfViewerHeight
                );
            }
            if (latestDocumentUploaded.startsWith('data:image')) {
                return renderImageAsDocument(latestDocumentUploaded);
            }
        } else if (typeof documentBase64 === 'string') {
            if (documentBase64.startsWith('data:image')) {
                return renderImageAsDocument(documentBase64);
            }

            if (
                documentBase64.startsWith('data:application') ||
                documentBase64.startsWith('blob:http')
            ) {
                return renderPdfDocument(
                    documentBase64,
                    documentName,
                    pdfViewerHeight
                );
            }
        }

        return null;
    };

    return (
        <Box className={classes.documentCover}>
            <Box className={classes.documentHeader}>
                <Box className={classes.documentTitle}>{documentName}</Box>
                <Box className={classes.viewerButtonsContainer}>
                    <Button
                        onClick={() => handleOpenInNewTab(documentBase64)}
                        className={launchButtonClass}
                    >
                        <LaunchIcon className={classes.buttonIcon} />
                    </Button>
                </Box>
            </Box>
            <div className={classes.documentInternals}>
                <div className={classes.documentContainer}>
                    {renderImagesAsDocOrAPdf()}
                </div>
            </div>
        </Box>
    );
};

CertificateReviewComponent.propTypes = {
    documentBase64: PropTypes.oneOf([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string
    ]),
    documentName: PropTypes.string,
    pdfViewerHeight: PropTypes.number
};

CertificateReviewComponent.defaultProps = {
    documentBase64: [],
    documentName: '',
    pdfViewerHeight: 0
};

const CertificateReview = withStyles(styles)(CertificateReviewComponent);

export default CertificateReview;
